<script lang="ts" setup>
import { useValidator } from '@/composeables';
import type { IEditorComponent } from '@/objects/gui';
import { watch, ref, type ComponentPublicInstance, computed } from 'vue';
import { IconSpinner, ValidatorMessage, type IInputSelectModelValueType, type IInputSelectPropsWithoutModelValue } from '@/components';
import { toValue } from 'vue';

defineOptions({ inheritAttrs: false });

// Why does this have a generic? It does not need to know what a row is
// TODO: Remove the TRow generic.
// TODO: Rewrite this to use the DropdownComp instead of being a browser select element
const props = withDefaults(defineProps<IInputSelectPropsWithoutModelValue>(), {
    defaultfirst: false,
    validator: null,
    label: undefined,
});

// The reference to the input
const inputref = ref<ComponentPublicInstance<IEditorComponent> | null>(null);

const modelValue = defineModel<IInputSelectModelValueType>({ required: true });

// We dont support async datasource in this component
if (typeof props.datasource === 'object' && props.datasource?.asyncsource) {
    throw new Error('asyncsource is not supported in this component');
}

// the datasource
const innerDatasource = computed(() => {
    if (typeof props.datasource === 'function') {
        return props.datasource(props.row);
    }

    return toValue(props.datasource?.source) ?? Array.from(toValue(props.datasource?.mapsource)?.values() ?? []);
});

// If we have a default first value and no value is set, set the first value
if (props.defaultfirst && !modelValue.value && innerDatasource.value.length > 0) {
    modelValue.value = innerDatasource.value[0].value;
}

// The validate function
const { Validate, ValidateMessage } = useValidator(props.validator ?? null, modelValue)!;

// If the input has had focus. We use this to show the validation message
const hasHadFocus = ref(false);

watch(
    () => modelValue.value,
    (nv) => (!props.validator ? null : Validate(nv?.toString() ?? 0)),
    { immediate: true },
);

defineExpose<IEditorComponent>({ focus: () => inputref.value?.focus(), validateMessage: ValidateMessage });
</script>
<template>
    <ValidatorMessage
        :message="ValidateMessage"
        :hide="!hasHadFocus"
    >
        <div class="input-group">
            <span class="flex-fill d-flex align-items-center">
                <label
                    v-if="props.label"
                    class="form-label textlabel text-end me-4"
                >
                    {{ $tm(props.label) }}
                </label>
                <div class="flex-fill d-flex">
                    <select
                        class="form-select form-control cell-renderer"
                        :class="props.class"
                        :disabled="$attrs.disabled ? true : false"
                        v-model="modelValue"
                        ref="inputref"
                        @blur="hasHadFocus = true"
                    >
                        <option
                            v-for="kv in innerDatasource"
                            :value="kv.value"
                            :key="kv.value?.toString() ?? ''"
                        >
                            {{ $tm(kv.text) }}
                        </option>
                    </select>
                    <span
                        class="input-group-text-inline"
                        v-if="state != null && state.isActive"
                    >
                        <IconSpinner
                            :state="state"
                            :show-success="false"
                        />
                    </span>
                </div>
            </span>
        </div>
    </ValidatorMessage>
</template>
